import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PreparationVue, type Blocage, type Etape, type Etat } from './preparation/PreparationVue';
import { ProfilTropEloigneError, QuotaError } from '../services/claude';
import { adapterCvDossier, analyserOffre, finaliserDossier, redigerLettreDossier } from '../services/offres';
import { usePaywallStore } from '../store/paywallStore';
import type { DossierFinal, EtapeAnalyse, EtapeCv, EtapeLettre, Offre } from '../types/offres';

export interface PreparationDossierProps {
  offre: Offre;
  onFermer: () => void;
  onPret?: (dossier: DossierFinal) => void;
  /** Offre trop éloignée du CV : fermer le détail aussi, pour revenir à la liste. */
  onRetourOffres?: () => void;
  /** Dossier prêt : ouvrir l'écran 5 de relecture. */
  onVerifier?: (dossierId: string) => void;
}

/**
 * Écran 4 — l'IA prépare la candidature. CHAQUE ÉTAPE SE COCHE À SON VRAI RETOUR : analyse, puis
 * CV et lettre en parallèle, puis contrôle. Aucune durée annoncée, aucune progression simulée (règle
 * du projet) : la barre suit les étapes terminées, rien d'autre. Une étape en échec se relance
 * seule — les étapes faites sont gardées côté serveur, et le crédit n'est décompté qu'à la fin.
 *
 * Affiché dans une fenêtre plein écran ouverte par-dessus le panneau de détail (FenetrePleinEcran).
 * Ce composant tient la LOGIQUE ; l'habillage et les animations vivent dans PreparationVue.
 */
export function PreparationDossier({ offre, onFermer, onPret, onRetourOffres, onVerifier }: PreparationDossierProps) {
  const [etats, setEtats] = useState<Record<Etape, Etat>>({ analyse: 'attente', cv: 'attente', lettre: 'attente', controle: 'attente' });
  const [analyse, setAnalyse] = useState<EtapeAnalyse | null>(null);
  const [cv, setCv] = useState<EtapeCv | null>(null);
  const [lettre, setLettre] = useState<EtapeLettre | null>(null);
  const [final, setFinal] = useState<DossierFinal | null>(null);
  const [blocage, setBlocage] = useState<Blocage | null>(null);
  const [raison, setRaison] = useState<string | null>(null);
  const [echec, setEchec] = useState(false);

  // Les étapes faites et l'identifiant du dossier vivent aussi dans des refs : une relance doit lire
  // l'état réel, pas celui capturé au rendu qui l'a déclenchée.
  const faites = useRef<Record<Etape, boolean>>({ analyse: false, cv: false, lettre: false, controle: false });
  const dossierId = useRef<string | null>(null);
  const monte = useRef(true);
  // Une préparation à la fois. « Réessayer » pouvait être pressé deux fois avant que l'écran ne réagisse :
  // deux analyses, deux dossiers qui s'écrasent, et deux tentatives de décompte du crédit.
  const enCours = useRef(false);
  useEffect(
    () => () => {
      monte.current = false;
    },
    [],
  );

  const marquer = (etape: Etape, etat: Etat) => {
    if (etat === 'fait') faites.current[etape] = true;
    if (monte.current) setEtats((avant) => ({ ...avant, [etape]: etat }));
  };

  const etape = useCallback(async <T,>(nom: Etape, appel: () => Promise<T>, garder: (r: T) => void): Promise<void> => {
    marquer(nom, 'en_cours');
    try {
      const r = await appel();
      if (monte.current) garder(r);
      marquer(nom, 'fait');
    } catch (e) {
      marquer(nom, 'erreur');
      throw e;
    }
  }, []);

  const lancer = useCallback(async () => {
    if (enCours.current) return;
    enCours.current = true;
    setEchec(false);
    try {
      if (!faites.current.analyse) {
        await etape('analyse', () => analyserOffre(offre), (a) => {
          dossierId.current = a.dossierId;
          setAnalyse(a);
        });
      }
      const id = dossierId.current as string;
      const paralleles = await Promise.allSettled([
        faites.current.cv ? Promise.resolve() : etape('cv', () => adapterCvDossier(id), setCv),
        faites.current.lettre ? Promise.resolve() : etape('lettre', () => redigerLettreDossier(id), setLettre),
      ]);
      // Le refus de la lettre passe avant une panne du CV : « Réessayer » relancerait sinon une lettre
      // que le modèle a déjà jugée impossible.
      const echecs = paralleles.filter((p): p is PromiseRejectedResult => p.status === 'rejected');
      const refus = echecs.find((p) => p.reason instanceof ProfilTropEloigneError) ?? echecs[0];
      if (refus) throw refus.reason;
      await etape('controle', () => finaliserDossier(id), (d) => {
        setFinal(d);
        onPret?.(d);
      });
    } catch (e) {
      if (!monte.current) return;
      if (e instanceof QuotaError) {
        usePaywallStore.getState().open('offre_dossier', e.reason);
        setBlocage('quota');
      } else if (e instanceof ProfilTropEloigneError) {
        // Pas de dossier sans lettre : on s'arrête là, et la finalisation, seule à décompter, n'a pas lieu.
        setRaison(e.raison);
        setBlocage('profil_trop_eloigne');
      } else if (e instanceof Error && e.message === 'offres_dossier_cv_manquant') setBlocage('cv_manquant');
      else if (e instanceof Error && e.message === 'offres_dossier_offre_retiree') setBlocage('offre_retiree');
      else setEchec(true);
    } finally {
      enCours.current = false;
    }
  }, [etape, offre, onPret]);

  useEffect(() => {
    void lancer();
    // Une seule préparation par ouverture de l'écran ; les relances passent par le bouton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PreparationVue
      offre={offre}
      etats={etats}
      analyse={analyse}
      cv={cv}
      lettre={lettre}
      final={final}
      blocage={blocage}
      raison={raison}
      echec={echec}
      onFermer={onFermer}
      onReessayer={() => void lancer()}
      onVerifier={() => (onVerifier && final ? onVerifier(final.id) : onFermer())}
      onRetourOffres={onRetourOffres ?? onFermer}
    />
  );
}
